import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Grid } from "semantic-ui-react";

import { AccordionTitleView, DropDown, InputText } from "../../../../../components";
import { FIELD_TYPES, FARMING_TYPES, CULTIVATION_TYPES } from "../../../../../config/constants";
import { useGetCountriesWithAlpha, useGetProduceType } from "../../../../../api/static-data";

import "../styles/CultivarForm.scss";
import { validateCharacterLength } from "../../../../../utils/utils";

type CultivarFormValues = {
  produceType: string;
  dataType: string;
  originCountry: string;
  farmingType: string;
  farmingField: string;
  region: string;
  latitude: number | null;
  longitude: number | null;
  produceCultivar?: string;
};

const CultivarField = forwardRef(
  ({ currentFormData, currentDataType = () => {}, currentOriginalFormData, onValidityChange }: any, ref) => {
    const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
    const [dataType, setDataType] = useState<string>("Supplier");
    const [produceType, setProduceType] = useState<any>();
    const [farmingType, setFarmingType] = useState<string>("Unknown");
    const [fieldType, setFieldType] = useState<string>("Unknown");
    const [country, setCountry] = useState<any>();

    const {
      data: produceTypes,
      isSuccess: isProduceTypesSuccess,
      isLoading: isProduceTypesLoading
    } = useGetProduceType();

    const {
      data: countries,
      isSuccess: isCountriesDataSuccess,
      isLoading: isCountriesDataLoading
    } = useGetCountriesWithAlpha();

    const enableAccodation = () => {
      setAccordionEnabled(!accordionEnabled);
    };

    const setCurrentProduceType = () => {
      const option = produceTypes?.find(
        (p: any) => parseInt(p.value) === parseInt(currentFormData?.produceType)
      );
      setProduceType(option?.value || "");
    };

    const setCurrentCountry = () => {
      const option = countries?.find(
        (c: any) => parseInt(c.value) === parseInt(currentFormData?.originCountry)
      );
      setCountry(option?.value || "");
    };

    const getDefaultCountry = useCallback(() => {
      const denmarkValue = countries?.find((e: any) => e.code === "DK");
      return denmarkValue?.key;
    }, [countries]);

    const values = useMemo(() => {
      const defaultFarmingType = FARMING_TYPES[0].value;
      const defaultProduceType = produceTypes?.[0]?.value;
      const defaultfieldType = FIELD_TYPES?.[0]?.value;
      setDataType(currentFormData?.dataType || "Supplier");
      currentDataType(currentFormData?.dataType || "Supplier");
      setCurrentProduceType();
      setCurrentCountry();
      setFarmingType(currentFormData?.farmingType || defaultFarmingType);
      setFieldType(currentFormData?.farmingField || defaultfieldType);
      setProduceType(
        currentFormData?.produceType != null
          ? String(currentFormData.produceType)
          : defaultProduceType
      );
      setCountry(currentFormData?.originCountry || getDefaultCountry());
      return {
        produceType: currentFormData?.produceType || defaultProduceType,
        dataType: currentFormData?.dataType || "",
        originCountry: currentFormData?.originCountry || getDefaultCountry(),
        farmingType: currentFormData?.farmingType || defaultFarmingType,
        farmingField: currentFormData?.farmingField || defaultfieldType,
        region: currentFormData?.region || "",
        latitude: currentFormData?.latitude || null,
        longitude: currentFormData?.longitude || null,
        produceCultivar: currentFormData?.produceCultivar || ""
      };
    }, [currentFormData, produceTypes, isProduceTypesSuccess, isCountriesDataSuccess]);

    const {
      register,
      formState: { errors, isValid },
      getValues,
      trigger,
      setValue
    } = useForm({ defaultValues: values, values: values, mode: "all", reValidateMode: "onChange" });

    useImperativeHandle(ref, () => ({
      getCultivationData: async () => {
        const isValid = await trigger();

        if (!isValid) {
          return null;
        }

        let currentCultivationData = getValues();
        currentCultivationData.dataType = dataType;
        currentCultivationData.produceType = produceType;
        currentCultivationData.farmingType = farmingType;
        currentCultivationData.farmingField = fieldType;
        return currentCultivationData;
      }
    }));

     useEffect(() => {
        onValidityChange(isValid);
      }, [isValid, onValidityChange]);

    return (
      <div>
        <AccordionTitleView accordionEnabled={false} title={"Cultivar"} plusIcon={false} />
        <div>
          <Grid>
            <Grid.Row divided>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Grid columns={2}>
                  <Grid.Row>
                    <Grid.Column>
                      <p className="textLabel mb-0">Data Type</p>
                      <p className="textLabel">{currentOriginalFormData?.dataType}</p>
                    </Grid.Column>
                    <Grid.Column style={{ marginBottom: 15 }}>
                      <p className="textLabel mb-0">Produce Type</p>
                      <p className="textLabel">
                        {
                          produceTypes?.find(
                            (p: any) =>
                              parseInt(p.value) === parseInt(currentOriginalFormData?.produceType)
                          )?.text
                        }
                      </p>
                    </Grid.Column>
                    <Grid.Column style={{ marginBottom: 15 }}>
                      <p className="textLabel mb-0">Origin Country</p>
                      <p className="textLabel">
                        {
                          countries?.find(
                            (p: any) =>
                              parseInt(p.value) === parseInt(currentOriginalFormData?.originCountry)
                          )?.text
                        }
                      </p>
                    </Grid.Column>
                    <Grid.Column>
                      <p className="textLabel mb-0">Produce Cultivar</p>
                      <p className="textLabel">{currentOriginalFormData?.produceCultivar}</p>
                    </Grid.Column>
                    <Grid.Column>
                      <p className="textLabel mb-0">Farming Type</p>
                      <p className="textLabel">{currentOriginalFormData?.farmingType}</p>
                    </Grid.Column>
                    <Grid.Column>
                      <p className="textLabel mb-0">Field Type</p>
                      <p className="textLabel">{currentOriginalFormData?.farmingField}</p>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Grid columns={2}>
                  <Grid.Row>
                    <Grid.Column computer={8}>
                      <DropDown
                        labelName="Data Type*"
                        placeholder="Select Type"
                        defaultValue={dataType}
                        currentData={CULTIVATION_TYPES}
                        handleChangeState={(e: any, { value }: any) => {
                          setDataType(value);
                          currentDataType(value);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={8}>
                      <DropDown
                        labelName="Produce Type*"
                        placeholder="Select Produce Type"
                        defaultValue={produceType}
                        currentData={produceTypes}
                        handleChangeState={(e: any, { value }: any) => {
                          setProduceType(value);
                        }}
                        loading={isProduceTypesLoading}
                        search
                        key={"produceType"}
                      />
                    </Grid.Column>
                    <Grid.Column computer={8}>
                      <DropDown
                        labelName="Origin Country"
                        placeholder="Select origin country"
                        defaultValue={country}
                        currentData={countries}
                        required
                        errorMessage={"Origin country required"}
                        handleChangeState={(e: any, { value }: any) => {
                          setCountry(value);
                          setValue("originCountry", value);
                        }}
                        loading={isCountriesDataLoading}
                        search
                        key={"country"}
                      />
                    </Grid.Column>
                    <Grid.Column computer={8}>
                      <InputText
                        labelName={"Produce Cultivar*"}
                        name="produceCultivar"
                        id="produceCultivar"
                        register={register}
                        required
                        errors={errors.produceCultivar}
                        errorMessage="Please enter produce cultivar"
                        validateHandle={(value: any) => {
                          validateCharacterLength(
                            value,
                            32,
                            "Produce Cultivar cannot exceed 32 characters"
                          );
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={8}>
                      <DropDown
                        labelName="Farming Type"
                        placeholder="Select Farming Type"
                        defaultValue={farmingType}
                        currentData={FARMING_TYPES}
                        handleChangeState={(e: any, { value }: any) => setFarmingType(value)}
                      />
                    </Grid.Column>
                    <Grid.Column computer={8}>
                      <DropDown
                        labelName="Field Type"
                        placeholder="Select Field Type"
                        defaultValue={fieldType}
                        currentData={FIELD_TYPES}
                        handleChangeState={(e: any, { value }: any) => setFieldType(value)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </div>
    );
  }
);

export default CultivarField;
